import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, ButtonBase, Chip, CircularProgress, Divider, Grid, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import AutocompleteList from '../../utils/componentes-utiles/AutocompleteList';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import Swal from 'sweetalert2';
import { cambiarProyectoTareaResumen, validarTareaResumen } from '../../services/apis';

export const ResumenTareasRealizadas = ({
    tareas = [],
    filtros,
    setFiltros,
    loadedFilters,
    opcionesUsuarios,
    selectedUsers,
    setSelectedUsers,
    opcionesClientes,
    selectedClientes,
    setSelectedClientes,
    onTareaEstadoCambiado,
    opcionesProyectos = [],
    onTareaProyectoCambiado
}) => {
    const [validandoTarea, setValidandoTarea] = useState(null);
    const [cambiandoProyectoTarea, setCambiandoProyectoTarea] = useState(null);

    const tareasFiltradas = useMemo(() => {
        const clientesSeleccionados = new Set(selectedClientes.map(cliente => Number(cliente.value)));
        const usuariosSeleccionados = new Set(selectedUsers.map(usuario => Number(usuario.value)));

        return tareas.filter(tarea => (
            (clientesSeleccionados.size === 0 || clientesSeleccionados.has(Number(tarea.id_cliente)))
            && (usuariosSeleccionados.size === 0 || usuariosSeleccionados.has(Number(tarea.id_usuario)))
        ));
    }, [tareas, selectedClientes, selectedUsers]);

    const convertirHoras = (minutos) => {
        const totalMinutos = Math.round(Number(minutos || 0));
        const horas = Math.floor(totalMinutos / 60);
        const minutosRestantes = totalMinutos % 60;
        return `${horas}h${minutosRestantes > 0 ? ` ${minutosRestantes}min` : ''}`;
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        const [year, month, day] = fecha.split('-');
        return `${day}/${month}/${year}`;
    };

    const normalizarColorEstado = (color) => {
        const colorTexto = String(color || '').trim();

        if (!colorTexto) return '#d7dce1';
        if (colorTexto.startsWith('#')) return colorTexto;
        if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(colorTexto)) {
            return `#${colorTexto}`;
        }

        return colorTexto;
    };

    const obtenerColorEstado = (tarea) =>
        normalizarColorEstado(tarea.estado_color || tarea.color_estado || tarea.color || '#d7dce1');

    const desplazarFecha = (fecha, dias) => {
        const fechaActual = new Date(`${fecha}T00:00:00`);
        fechaActual.setDate(fechaActual.getDate() + dias);

        const year = fechaActual.getFullYear();
        const month = String(fechaActual.getMonth() + 1).padStart(2, '0');
        const day = String(fechaActual.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const desplazarRango = (dias) => {
        setFiltros(prev => ({
            fechaDesde: desplazarFecha(prev.fechaDesde, dias),
            fechaHasta: desplazarFecha(prev.fechaHasta, dias)
        }));
    };

    const seleccionarHoy = () => {
        const hoy = new Date();
        const year = hoy.getFullYear();
        const month = String(hoy.getMonth() + 1).padStart(2, '0');
        const day = String(hoy.getDate()).padStart(2, '0');
        const fechaHoy = `${year}-${month}-${day}`;

        setFiltros({ fechaDesde: fechaHoy, fechaHasta: fechaHoy });
    };

    const seleccionarUsuario = (usuario) => {
        setSelectedUsers(prev => {
            const usuarioYaSeleccionado = prev.some(item => Number(item.value) === Number(usuario.id));
            if (usuarioYaSeleccionado) return prev;

            const opcionUsuario = opcionesUsuarios.find(item => Number(item.value) === Number(usuario.id));
            return [...prev, opcionUsuario || { value: usuario.id, label: usuario.nombre }];
        });
    };

    const handleValidarTarea = async (tarea) => {
        const estaValidada = Number(tarea.id_estado) === 4;
        const confirm = await Swal.fire({
            icon: 'question',
            title: estaValidada ? 'Desvalidar tarea' : 'Validar tarea',
            text: estaValidada
                ? `La tarea #${tarea.id_tarea} volvera a estado finalizada.`
                : `La tarea #${tarea.id_tarea} quedara validada.`,
            showCancelButton: true,
            confirmButtonText: estaValidada ? 'Desvalidar' : 'Validar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: estaValidada ? '#1976d2' : '#2e7d32',
        });

        if (!confirm.isConfirmed) return;

        setValidandoTarea(tarea.id_tarea);
        const response = await validarTareaResumen(tarea.id_tarea, !estaValidada);
        setValidandoTarea(null);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo actualizar',
                text: response?.message || 'Revisa la tarea e intentalo de nuevo.',
            });
            return;
        }

        if (onTareaEstadoCambiado) {
            onTareaEstadoCambiado(response.data);
        }
    };

    const obtenerProyectoSeleccionado = (tarea) => (
        opcionesProyectos.find(proyecto => Number(proyecto.value) === Number(tarea.id_proyecto))
        || { label: tarea.nombre_proyecto || 'Sin proyecto', value: tarea.id_proyecto || '' }
    );

    const handleCambiarProyecto = async (tarea, proyecto) => {
        if (!proyecto || Number(proyecto.value) === Number(tarea.id_proyecto)) return;

        const confirm = await Swal.fire({
            icon: 'question',
            title: 'Cambiar proyecto',
            text: `La tarea #${tarea.id_tarea} se movera al proyecto ${proyecto.label}.`,
            showCancelButton: true,
            confirmButtonText: 'Cambiar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#1976d2',
        });

        if (!confirm.isConfirmed) return;

        setCambiandoProyectoTarea(tarea.id_tarea);
        const response = await cambiarProyectoTareaResumen(tarea.id_tarea, proyecto.value);
        setCambiandoProyectoTarea(null);

        if (!response?.success) {
            Swal.fire({
                icon: 'error',
                title: 'No se pudo cambiar',
                text: response?.message || 'Revisa la tarea e intentalo de nuevo.',
            });
            return;
        }

        if (onTareaProyectoCambiado) {
            onTareaProyectoCambiado(response.data);
        }
    };

    const resumenUsuarios = useMemo(() => {
        const resumen = new Map();

        tareas.forEach((tarea) => {
            const idUsuario = Number(tarea.id_usuario);
            const datosUsuario = resumen.get(idUsuario) || {
                id: idUsuario,
                nombre: tarea.usuario,
                minutos: 0,
                tareas: 0,
                proyectos: new Map()
            };

            datosUsuario.minutos += Number(tarea.minutos || 0);
            datosUsuario.tareas += 1;
            const nombreProyecto = tarea.nombre_proyecto || 'Sin proyecto';
            datosUsuario.proyectos.set(
                nombreProyecto,
                (datosUsuario.proyectos.get(nombreProyecto) || 0) + Number(tarea.minutos || 0)
            );
            resumen.set(idUsuario, datosUsuario);
        });

        return Array.from(resumen.values())
            .map(usuario => ({
                ...usuario,
                proyectos: Array.from(usuario.proyectos.entries())
                    .map(([nombre, minutos]) => ({ nombre, minutos }))
                    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }, [tareas]);

    const usuariosSinHoras = useMemo(() => {
        const usuariosConHoras = new Set(resumenUsuarios.map(usuario => Number(usuario.id)));

        return opcionesUsuarios
            .filter(usuario => !usuariosConHoras.has(Number(usuario.value)))
            .map(usuario => ({
                id: usuario.value,
                nombre: usuario.label,
                minutos: 0
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }, [opcionesUsuarios, resumenUsuarios]);

    const resumenProyectos = useMemo(() => {
        const resumen = new Map();

        tareas.forEach((tarea) => {
            const nombreProyecto = tarea.nombre_proyecto || 'Sin proyecto';
            const datosProyecto = resumen.get(nombreProyecto) || {
                nombre: nombreProyecto,
                minutos: 0,
                tareas: 0,
                usuarios: new Map()
            };

            datosProyecto.minutos += Number(tarea.minutos || 0);
            datosProyecto.tareas += 1;
            datosProyecto.usuarios.set(
                tarea.usuario,
                (datosProyecto.usuarios.get(tarea.usuario) || 0) + Number(tarea.minutos || 0)
            );
            resumen.set(nombreProyecto, datosProyecto);
        });

        return Array.from(resumen.values())
            .map(proyecto => ({
                ...proyecto,
                usuarios: Array.from(proyecto.usuarios.entries())
                    .map(([nombre, minutos]) => ({ nombre, minutos }))
                    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
            }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }, [tareas]);

    const totalMinutos = tareasFiltradas.reduce((total, tarea) => total + Number(tarea.minutos || 0), 0);

    return (
        <Paper
            variant="outlined"
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                p: { xs: 1.5, md: 2 },
                borderRadius: 1,
                boxSizing: 'border-box',
                overflow: 'hidden',
                borderColor: '#d7dce1',
                backgroundColor: '#fbfcfd'
            }}
        >
            <Box
                sx={{
                    p: 1,
                    border: '1px solid #e0e4e8',
                    borderRadius: 1,
                    backgroundColor: '#fff'
                }}
            >
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                            <AssignmentTurnedInOutlinedIcon color="primary" sx={{ fontSize: 21 }} />
                            <Typography variant="subtitle1" fontWeight={700}>Resumen de tareas</Typography>
                            {!loadedFilters && <CircularProgress size={17} />}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.25}
                        >
                            <Tooltip title="Día anterior">
                                <IconButton size="small" onClick={() => desplazarRango(-1)} aria-label="Día anterior">
                                    <ChevronLeftIcon />
                                </IconButton>
                            </Tooltip>
                            <TextField
                                label="Desde"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filtros.fechaDesde}
                                onChange={(event) => setFiltros(prev => ({ ...prev, fechaDesde: event.target.value }))}
                                fullWidth
                                sx={{ minWidth: 0, flex: '1 1 0', backgroundColor: '#fff' }}
                            />
                            <TextField
                                label="Hasta"
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filtros.fechaHasta}
                                onChange={(event) => setFiltros(prev => ({ ...prev, fechaHasta: event.target.value }))}
                                fullWidth
                                sx={{ minWidth: 0, flex: '1 1 0', backgroundColor: '#fff' }}
                            />
                            <Tooltip title="Día siguiente">
                                <IconButton size="small" onClick={() => desplazarRango(1)} aria-label="Día siguiente">
                                    <ChevronRightIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Volver a hoy">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={seleccionarHoy}
                                    aria-label="Volver a hoy"
                                >
                                    <TodayOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ minWidth: 0 }}>
                        <AutocompleteList
                            key={selectedUsers.map(usuario => usuario.value).sort().join('-')}
                            array={opcionesUsuarios}
                            setArray={setSelectedUsers}
                            arrayPre={selectedUsers}
                            label="Usuarios"
                        />
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ minWidth: 0 }}>
                        <AutocompleteList
                            array={opcionesClientes}
                            setArray={setSelectedClientes}
                            arrayPre={selectedClientes}
                            label="Clientes"
                        />
                    </Grid>
                </Grid>
            </Box>

            {tareasFiltradas.length > 0 && (
                <Box
                    sx={{
                        px: 2,
                        pb: 0.75,
                        backgroundColor: '#f7f9fb'
                    }}
                >
                    <Grid
                        container
                        alignItems="center"
                        rowSpacing={0.35}
                        sx={{ minWidth: 0, display: { xs: 'none', sm: 'flex' } }}
                    >
                        <Grid item xs={6} sm={1.25}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>Fecha</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>Usuario</Typography>
                        </Grid>
                        <Grid item xs={6} sm={1.75}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>Cliente</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2} sx={{ order: { xs: 4, sm: 4 } }}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>Proyecto</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3.5} sx={{ order: { xs: 6, sm: 5 } }}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>Tarea</Typography>
                        </Grid>
                        <Grid item xs={6} sm={1.5} textAlign="center" sx={{ order: { xs: 5, sm: 6 } }}>
                            <Typography color="textSecondary" fontWeight={700} sx={{ fontSize: 11 }}>
                                Horas empleadas
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 2,
                    mb: 1.5
                }}
            >
                <Chip
                    icon={<AssignmentTurnedInOutlinedIcon />}
                    label={`${tareasFiltradas.length} registros de tareas`}
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                />
                <Chip
                    icon={<AccessTimeOutlinedIcon />}
                    label={`${convertirHoras(totalMinutos)} empleadas`}
                    variant="outlined"
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>

            <Box
                sx={{
                    height: { xs: 360, sm: 420, md: 470 },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: { xs: 0.5, md: 1 },
                    maxWidth: '100%'
                }}
            >
                {tareasFiltradas.length === 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
                        <Typography color="textSecondary">
                            No hay tareas registradas para los filtros seleccionados.
                        </Typography>
                    </Box>
                )}

                <Grid container rowGap={0.65}>
                    {tareasFiltradas.map((tarea) => {
                        const colorEstado = obtenerColorEstado(tarea);

                        return (
                        <Paper
                            key={`${tarea.fecha}-${tarea.id_tarea}-${tarea.id_usuario}`}
                            variant="outlined"
                            sx={{
                                width: '100%',
                                px: { xs: 1, sm: 1.35 },
                                py: 0.75,
                                borderRadius: 1,
                                overflow: 'hidden',
                                background: `linear-gradient(90deg, ${colorEstado}18 0%, #fff 24%)`,
                                borderColor: `${colorEstado}66`,
                                borderLeft: `5px solid ${colorEstado}`,
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    borderColor: colorEstado,
                                    boxShadow: `0 3px 10px ${colorEstado}30`
                                }
                            }}
                        >
                            <Grid container alignItems="center" rowSpacing={{ xs: 0.35, sm: 0.45 }} columnSpacing={{ xs: 0.8, sm: 0 }} sx={{ minWidth: 0 }}>
                                <Grid item xs={4} sm={1.25}>
                                    <Typography fontWeight={600} sx={{ fontSize: 12.5 }}>{formatearFecha(tarea.fecha)}</Typography>
                                </Grid>
                                <Grid item xs={5} sm={2}>
                                    <Typography noWrap sx={{ fontSize: 12.5 }}>{tarea.usuario}</Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    sm={1.5}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: { xs: 'flex-end', sm: 'center' },
                                        order: { xs: 3, sm: 6 }
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent={{ xs: 'flex-end', sm: 'center' }}
                                        gap={0.35}
                                        width="100%"
                                    >
                                        <AccessTimeOutlinedIcon color="primary" sx={{ fontSize: 15 }} />
                                        <Typography fontWeight={700} sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{convertirHoras(tarea.minutos)}</Typography>
                                        {[3, 4].includes(Number(tarea.id_estado)) && (
                                            <Tooltip title={Number(tarea.id_estado) === 4 ? 'Desvalidar tarea' : 'Validar tarea'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleValidarTarea(tarea)}
                                                        disabled={validandoTarea === tarea.id_tarea}
                                                        sx={{
                                                            p: 0.25,
                                                            ml: 0.2,
                                                            color: Number(tarea.id_estado) === 4 ? '#1976d2' : '#2e7d32',
                                                        }}
                                                    >
                                                        {validandoTarea === tarea.id_tarea ? (
                                                            <CircularProgress size={14} />
                                                        ) : Number(tarea.id_estado) === 4 ? (
                                                            <UndoIcon sx={{ fontSize: 16 }} />
                                                        ) : (
                                                            <FactCheckOutlinedIcon sx={{ fontSize: 16 }} />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={6} sm={1.75} sx={{ order: { xs: 4, sm: 3 } }}>
                                    <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{tarea.cliente || 'Sin cliente'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={2} sx={{ order: { xs: 5, sm: 4 } }}>
                                    <Autocomplete
                                        size="small"
                                        disableClearable
                                        options={opcionesProyectos}
                                        value={obtenerProyectoSeleccionado(tarea)}
                                        loading={cambiandoProyectoTarea === tarea.id_tarea}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                                        onChange={(event, newValue) => handleCambiarProyecto(tarea, newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="standard"
                                                placeholder="Proyecto"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    disableUnderline: true,
                                                    sx: {
                                                        fontSize: 12,
                                                        color: 'text.secondary',
                                                        fontWeight: 600,
                                                        minHeight: 22,
                                                        py: 0,
                                                    },
                                                }}
                                            />
                                        )}
                                        sx={{
                                            '& .MuiAutocomplete-inputRoot': {
                                                py: '0 !important',
                                                pr: '22px !important',
                                            },
                                            '& .MuiAutocomplete-input': {
                                                minWidth: '0 !important',
                                                p: '0 !important',
                                            },
                                            '& .MuiAutocomplete-endAdornment': {
                                                right: 0,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3.5} sx={{ order: { xs: 6, sm: 5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                                        <Tooltip title={tarea.nombre_estado || 'Estado de la tarea'}>
                                            <Box
                                                sx={{
                                                    width: 9,
                                                    height: 9,
                                                    borderRadius: '50%',
                                                    backgroundColor: colorEstado,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Tooltip>
                                        <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere', fontSize: 12.5, minWidth: 0 }}>
                                            {tarea.id_tarea} - {tarea.nombre_tarea}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                        );
                    })}
                </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
                <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                    <PersonOutlineOutlinedIcon color="primary" />
                    <Typography fontWeight={700}>Total de horas por usuario</Typography>
                </Box>

                {resumenUsuarios.length === 0 && usuariosSinHoras.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No hay usuarios disponibles.
                    </Typography>
                ) : (
                    <Box>
                        <Grid container spacing={1}>
                            {resumenUsuarios.map((usuario) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={usuario.id}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 1,
                                            backgroundColor: '#fff',
                                            borderColor: '#e0e4e8'
                                        }}
                                    >
                                        <Tooltip title="Filtrar tareas de este usuario">
                                            <ButtonBase
                                                onClick={() => seleccionarUsuario(usuario)}
                                                sx={{
                                                    display: 'block',
                                                    maxWidth: '100%',
                                                    textAlign: 'left',
                                                    color: 'primary.main'
                                                }}
                                            >
                                                <Typography
                                                    fontWeight={700}
                                                    sx={{ overflowWrap: 'anywhere', textDecoration: 'underline' }}
                                                >
                                                    {usuario.nombre}
                                                </Typography>
                                            </ButtonBase>
                                        </Tooltip>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                {usuario.tareas} registros
                                            </Typography>
                                            <Typography fontWeight={700} color="primary.main">
                                                {convertirHoras(usuario.minutos)}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 0.75 }} />
                                        <Box display="flex" flexDirection="column" gap={0.35}>
                                            {usuario.proyectos.map(proyecto => (
                                                <Box
                                                    key={proyecto.nombre}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    gap={1}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        sx={{ overflowWrap: 'anywhere' }}
                                                    >
                                                        {proyecto.nombre}
                                                    </Typography>
                                                    <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                                        {convertirHoras(proyecto.minutos)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        {usuariosSinHoras.length > 0 && (
                            <Grid container spacing={1} sx={{ mt: 0.25 }}>
                                {usuariosSinHoras.map((usuario) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={usuario.id}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 1,
                                                backgroundColor: '#f7f9fb',
                                                borderColor: '#e0e4e8'
                                            }}
                                        >
                                            <Tooltip title="Filtrar tareas de este usuario">
                                                <ButtonBase
                                                    onClick={() => seleccionarUsuario(usuario)}
                                                    sx={{
                                                        display: 'block',
                                                        maxWidth: '100%',
                                                        textAlign: 'left',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        sx={{ overflowWrap: 'anywhere', textDecoration: 'underline' }}
                                                    >
                                                        {usuario.nombre}
                                                    </Typography>
                                                </ButtonBase>
                                            </Tooltip>
                                            <Typography variant="caption" color="textSecondary">
                                                0h
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
                <Box display="flex" alignItems="center" gap={0.75} mb={1}>
                    <FolderOutlinedIcon color="primary" />
                    <Typography fontWeight={700}>Total de horas por proyecto</Typography>
                </Box>

                {resumenProyectos.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                        No hay horas registradas para los filtros seleccionados.
                    </Typography>
                ) : (
                    <Grid container spacing={1}>
                        {resumenProyectos.map((proyecto) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={proyecto.nombre}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 1,
                                        backgroundColor: '#fff',
                                        borderColor: '#e0e4e8'
                                    }}
                                >
                                    <Typography fontWeight={700} color="primary.main" sx={{ overflowWrap: 'anywhere' }}>
                                        {proyecto.nombre}
                                    </Typography>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            {proyecto.tareas} registros
                                        </Typography>
                                        <Typography fontWeight={700} color="primary.main">
                                            {convertirHoras(proyecto.minutos)}
                                        </Typography>
                                    </Box>
                                    <Divider sx={{ my: 0.75 }} />
                                    <Box display="flex" flexDirection="column" gap={0.35}>
                                        {proyecto.usuarios.map(usuario => (
                                            <Box
                                                key={usuario.nombre}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                gap={1}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    sx={{ overflowWrap: 'anywhere' }}
                                                >
                                                    {usuario.nombre}
                                                </Typography>
                                                <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                                                    {convertirHoras(usuario.minutos)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Paper>
    );
};
